<template>
  <teleport to="body">
    <transition
      enter-active-class="transition-opacity duration-150"
      enter-from-class="opacity-0"
      leave-active-class="transition-opacity duration-150"
      leave-to-class="opacity-0"
    >
      <div
        v-if="open"
        ref="dialogRef"
        v-bind="$attrs"
        class="fixed inset-0 flex items-center justify-center px-2 sm:px-4"
        :class="zClass"
        role="dialog"
        aria-modal="true"
        tabindex="-1"
      >
        <div
          class="absolute inset-0 bg-onyx/80 backdrop-blur-sm"
          aria-hidden="true"
          @click="dismiss"
        />

        <div
          class="relative flex max-h-[90vh] w-full flex-col overflow-hidden rounded-2xl border border-depth-3 bg-depth-1 shadow-xl"
          :class="maxWidth"
        >
          <!-- Outside the scrolling element, or it leaves the viewport on a dialog taller than the panel. -->
          <button
            v-if="dismissable"
            type="button"
            class="absolute top-3 right-3 z-10 size-9 rounded-full flex items-center justify-center text-rhino transition-colors hover:text-mouse hover:bg-depth-2"
            :aria-label="t('frontend/close')"
            @click="emit('close')"
          >
            <fa-icon :icon="faXmark" class="size-5" aria-hidden="true" />
          </button>

          <div
            class="flex min-h-0 flex-col overflow-y-auto"
            :class="panelClass"
          >
            <slot />
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup lang="ts">
import { faXmark } from '@fortawesome/pro-solid-svg-icons';

defineOptions({ inheritAttrs: false });

const props = withDefaults(
  defineProps<{
    open: boolean;
    maxWidth?: string;
    panelClass?: string;
    zClass?: string;
    dismissable?: boolean;
    dismissOnBackdrop?: boolean;
  }>(),
  {
    maxWidth: 'max-w-lg',
    panelClass: '',
    zClass: 'z-1050',
    dismissable: true,
    dismissOnBackdrop: true,
  },
);

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const { t } = useI18n();
const { lock, unlock } = useDialogScrollLock();

const dialogRef = ref<HTMLElement | null>(null);

function dismiss(): void {
  if (props.dismissable && props.dismissOnBackdrop) emit('close');
}

// On document, not the overlay: the overlay only takes focus once it is open, so
// a @keydown.esc on it misses a press that arrives before the focus lands.
function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape' && props.open && props.dismissable) {
    emit('close');
  }
}

// Bound only while open — several instances are mounted at once per page.
watch(
  () => props.open,
  async (open) => {
    if (!import.meta.client) return;
    if (open) {
      lock();
      document.addEventListener('keydown', onKeydown);
      await nextTick();
      dialogRef.value?.focus();
    } else {
      unlock();
      document.removeEventListener('keydown', onKeydown);
    }
  },
  { immediate: true },
);

onUnmounted(() => {
  if (import.meta.client) document.removeEventListener('keydown', onKeydown);
});
</script>
